import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Copy } from 'lucide-react';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';
import {
  Card,
  SetRow,
  Switch,
  Field,
  Select,
  Textarea,
  Btn,
} from '../ui/primitives';

/**
 * Public learner registration settings — rendered as the Register tab INSIDE
 * the Settings page (no page chrome of its own). The old standalone
 * /Portal/Instructor/Register route redirects to Settings?tab=register.
 */
export function RegistrationSettingsCard() {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [enableRegistrationPage, setEnableRegistrationPage] = useState(0);
  const [instructions, setInstructions] = useState("");
  const userId = localStorage.getItem("userId");
  const [theme, setTheme] = useState('');
  const [url, setUrl] = useState('');

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const handleEnableChange = (e) => {
    if (!guardAction('action.registration.enablePage')) return;
    const checked = e.target.checked ? 1 : 0;
    setEnableRegistrationPage(checked);
    setUrl(checked ? `https://instructor.studyak.ai/register/${userId}` : "");
  };

  const handleCopy = () => {
    if (!guardAction('action.registration.copyUrl')) return;
    if (url) {
      navigator.clipboard.writeText(url);
      alert("URL copied to clipboard!");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      try {
        const res = await axios.get(apiUrl+`/api/registration/${userId}`, {
          headers: {
            'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
          }
        });
        if (res.data) {
          setEnableRegistrationPage(res.data.enableRegistrationPage || 0);
          setTheme(res.data.theme || "");
          setInstructions(res.data.instructions || "");
          if (res.data.enableRegistrationPage === 1) {
            setUrl(res.data.generatedUrl);
          }
        }
      } catch (err) {
        console.error("Error fetching registration settings:", err);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Handle save/update
  const handleSave = async () => {
    if (!guardAction('action.registration.saveSettings')) return;
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const payload = {
        userId,
        enableRegistrationPage,
        theme,
        instructions,
        generatedUrl: enableRegistrationPage === 1 ? `https://instructor.studyak.ai/register/${userId}` : "",
      };

      await axios.post(apiUrl+`/api/registration/${userId}`, payload, {
        headers: {
          'Content-Type': "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          'Authorization': getBasicAuthHeader(),
        }
      });
      alert("Registration settings saved!");
    } catch (err) {
      console.error("Error saving registration settings:", err);
    }
  };

  return (
    <>
      <Card $pad>
        <div style={{ marginBottom: '20px', borderBottom: '1px solid #e5e5ea', paddingBottom: '8px' }}>
          <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700 }}>Registration Settings</h3>
          <p style={{ margin: 0, fontSize: '12.5px', color: '#7e8298' }}>
            Let learners self-register through a branded public link.
          </p>
        </div>

        {/* Enable Registration */}
        <SetRow>
          <div className="set-row__txt">
            <h4>Enable registration page</h4>
            <p>Publish a public sign-up link so learners can register themselves</p>
          </div>
          <Switch
            $on={enableRegistrationPage === 1}
            onClick={() => handleEnableChange({ target: { checked: enableRegistrationPage !== 1 } })}
          />
        </SetRow>

        {/* Theme */}
        <Field style={{ marginTop: '18px' }}>
          <label>Theme</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Select value={theme} onChange={(e) => setTheme(e.target.value)}>
              <option value="">Select theme</option>
              <option value="#7B1FA2">Custom Site Color 1</option>
              <option value="#3949AB">Custom Site Color 2</option>
              <option value="#00897B">Custom Site Color 3</option>
            </Select>
            {theme && (
              <span
                aria-hidden
                style={{
                  width: '28px',
                  height: '28px',
                  flex: 'none',
                  borderRadius: '8px',
                  background: theme,
                  boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
                }}
              />
            )}
          </div>
        </Field>

        {/* Instructions */}
        <Field>
          <label>Instructions</label>
          <Textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Enter instructions for registrants"
            rows={3}
          />
        </Field>

        {/* URL */}
        {enableRegistrationPage === 1 && (
          <Field>
            <label>Registration URL</label>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '0 13px',
                height: '40px',
                borderRadius: '10px',
                border: '1px solid #e5e5ea',
                background: '#f6f6f9',
              }}
            >
              <span style={{ fontSize: '13px', color: '#3a3a44', wordBreak: 'break-all' }}>{url}</span>
              <Btn $variant="ghost" $sm type="button" onClick={handleCopy}>
                <Copy size={14} /> Copy
              </Btn>
            </div>
          </Field>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
          <Btn onClick={handleSave}>
            <Check size={14} /> Save settings
          </Btn>
        </div>
      </Card>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}

export default RegistrationSettingsCard;
